import MultiProgress from 'react-multi-progress';
import styles from './stats-overview.module.scss';
import {  useEffect, useRef, useState } from 'react';

import { DonutGraph, StatsOverviewSkeleton, Shimmer } from '@flexo/atoms';
import { capitalizeFirstLetter, formatToMaxFourDigits } from '@flexo/general';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { Haptics } from '@capacitor/haptics';
import { useLocation } from 'react-router-dom';
import { useKpiData } from '@flexo/hooks';

interface StatsOverviewProps {
  title: string;
  type?: string;
  dataInTimeSpan?: boolean;
  view?: string;
  values?: Array<{
    label: string;
    totalPercentage: string;
    totalEnergyValue: string;
    id: string;
    progress?: Array<{
      value: string;
      color: string;
    }>;
  }>;
  graphDataKeys: Array<{
    key: string;
    label: string;
    percentage?: string;
  }>;
  graphColors?: string[];
  totalValue: string;
  isWeb?: boolean;
  count?: number;
}

export function StatsOverview(props: StatsOverviewProps) {
  const {
    StatsOverview__PieContainer,
    StatsOverview__Stats,
    StatsOverview__Stats__Title,
    StatsOverview__Stats__Percentages,
    StatsOverview__Stats__Percentages__Labels,
    StatsOverview__Stats__Percentages__Labels__Label,
  } = styles;

  const ENV = (import.meta as any).env;
  const browserLanguage = navigator.language;



  const { t } = useTranslation();
  const kpi = useSelector((state: any) => state.kpi);
  
  const { title, dataInTimeSpan = true, } = props;
  const isWeb = ENV?.VITE_APP_IS_WEB; 

  const ThemeStore = useSelector((state: any) => state.theme);
  const holdTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];

  let entity = 'Site';
  let kpiContext: string = 'Energy';

  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }

  if (pathSegment?.includes('community')) {
    entity = 'Community';
  }
  if (pathSegment?.includes('member')) {
    entity = 'Member';
  }
  
  const overviewKpiApiData = kpi[`${kpiContext}${entity}`]?.['1day']?.[0]?.kpi as any || null;
  const LOADING_STATE = kpi?.[`${kpiContext}KpiData${entity}AsyncStatus`];

  // Use the new hook for KPI data access
  const { findApiValue, findApiUnit } = useKpiData(overviewKpiApiData, kpiContext.toLowerCase());

  const graphData = props.graphDataKeys.map((data, index) => ({
    id: data.key,
    label: data.label,
    value: isWeb
      ? findApiValue(data.percentage)
      : findApiValue(data.key)
      ? findApiValue(data.key)
      : index === 0
      ? 100
      : 0,
    formattedValue: !overviewKpiApiData ? '-.-' : findApiValue(data.key),
  }));


  let displayItems;
  if (isWeb) {
    displayItems = graphData.map((data, itemIndex) => { // `data` is the current item like "Self-consumption"
      const individualProgressSegments = graphData.map((segment, segmentIndex) => {
        const colorKey = props.graphColors?.[segmentIndex];
        let segmentColor;
        if (segmentIndex === itemIndex) { // This is the segment for the current row
          segmentColor = (colorKey ? ThemeStore.colors[colorKey] : undefined) || '#E0E0E0'; // Actual color
        } else { // Other segments
          segmentColor = '#F0F0F0'; // Background/neutral color
        }
        return {
          value: segment.value, // All segments retain their original value
          color: segmentColor,
          className: styles.StatsOverview__MultiProgress__Element,
        };
      });

      return {
        label: data.label,
        totalPercentage:  !overviewKpiApiData ? '-' : data.value, // Already a percentage from findApiValue(data.percentage)
        totalEnergyValue:  data.formattedValue,
        id: data.id,
        progress: individualProgressSegments,
      };
    });
  } else {
    displayItems = props.values?.map((value) => ({
      label:  value.label,
      totalPercentage: !overviewKpiApiData ? '-' : findApiValue(value.totalPercentage)?.toLocaleString(browserLanguage),
      totalEnergyValue: formatToMaxFourDigits( findApiValue(value.totalEnergyValue)),
      id: value.id,
      progress: value?.progress?.map((progress) => ({
        value: findApiValue(progress.value),
        color: ThemeStore.colors[progress.color],
        className: styles.StatsOverview__MultiProgress__Element,
      })),
    }));
  }
  
  const startHold = (valueItem) => {
    if (selected !== null || (isWeb && valueItem.totalPercentage === 0)) return;
    else {
      setSelected(valueItem.id);
      // Add haptic feedback when hold starts (only on mobile)
      if (!isWeb) {
        Haptics.vibrate({ duration: 10 });
      }
      holdTimeout.current = setTimeout(() => {
        // Do nothing if held for the full duration
      }, 2000);
    }
  };

  const endHold = () => {
    if (holdTimeout.current) {
      clearTimeout(holdTimeout.current);
    }
    setSelected(null);
  };

  useEffect(() => {
    if (!isWeb) {
      const handleContextMenu = (event) => event.preventDefault();
      const handleCopy = (event) => event.preventDefault();
      const handlePaste = (event) => event.preventDefault();

      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('copy', handleCopy);
      document.addEventListener('paste', handlePaste);

      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('copy', handleCopy);
        document.removeEventListener('paste', handlePaste);
      };
    }
  }, [isWeb]);

  const rootClassName = isWeb ? `${styles.StatsOverview} ${styles.StatsOverview__Web}` : styles.StatsOverview;
  const itemMarginTop = isWeb ? '.75em' : '.5em';
  
  if (LOADING_STATE === 'PENDING' && !overviewKpiApiData && !isWeb) {
    return <StatsOverviewSkeleton title={title} count={props.values?.length || props.count || 3} />;
  }

  return (
    <div className={rootClassName} style={{ position: 'relative' }}>
      {LOADING_STATE === 'PENDING' && (
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%',
          height: '100%',
          zIndex: 999999,
          overflow: 'hidden'
        }}>
          <div style={
            ENV.VITE_APP_IS_WEB ? {
              width: 'calc(100% - 5px)',
              height: 'calc(100% - 20px)',
              position: 'absolute',
              top: 5,
              left: 5
            }
              : {
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: -10,
                right: -10,
              }}><Shimmer /></div>
        </div>
      )}
      <div className={StatsOverview__PieContainer}>
        <DonutGraph
          dataInTimeSpan={dataInTimeSpan}
          width={105}
          height={105}
          graphData={graphData}
          colors={
            ( (graphData && graphData.length > 0 && graphData[0].value === 100))
              ? (props?.graphColors?.map((color, index) =>
                   index === 0 ? '#F0F0F0' : '#FFFFFF'
                )) ?? (props.graphDataKeys && props.graphDataKeys.length > 0 ? [ '#F0F0F0' ] : []) // Fallback for loading colors
              : (props?.graphColors?.map((color) => ThemeStore.colors[color] || '#CCCCCC')) ?? [] // Fallback for theme colors
          }
          totalAmount={!overviewKpiApiData ? '-.-' : formatToMaxFourDigits(findApiValue(props.totalValue))}
          keys={props.graphDataKeys.map((k: { key: string; label: string; percentage?: string }) => k.key) as string[]}
          totalAmounttype={
            findApiUnit(props.totalValue) === ''
              ? 'kWh'
              : findApiUnit(props.totalValue)
          }
          selectedId={selected}
          setSelectedId={setSelected}
        />
      </div>{' '}
      <div
        className={StatsOverview__Stats}
        style={{
          marginTop: itemMarginTop,
        }}
      >
        <span className={`${StatsOverview__Stats__Title} heading4`}>
          {capitalizeFirstLetter(t(title))}
        </span>
        {displayItems?.map((value: any, index) => (
          <div
            className={StatsOverview__Stats__Percentages}
            key={isWeb ? `${value.label}-${index}` : value.id || value.label}
            style={{
              marginTop: itemMarginTop,
              cursor: 'pointer',
            }}
            onTouchStart={() => startHold(value)}
            onTouchEnd={endHold}
            onMouseEnter={() => startHold(value)}
            onMouseLeave={endHold}
          >
            <div
              className={`${StatsOverview__Stats__Percentages__Labels} paragraph ${
                styles.noSelect
              } ${selected === value.id ? styles.selected : ''}`}
            >
              <span
                className={`${StatsOverview__Stats__Percentages__Labels__Label} ${styles.noSelect}`}
              >
                {t(value.label)}
              </span>
              <span
                className={`${StatsOverview__Stats__Percentages__Labels__Label} heading5L ${
                  selected === value.id ? styles.selected : ''
                }`}
              >
                {value.totalPercentage}%
                {isWeb && !overviewKpiApiData ? '| -- kWh' : value.totalEnergyValue > 0 && ` | ${ formatToMaxFourDigits(value?.totalEnergyValue)} ${findApiUnit(value?.id)}`}
              </span>
            </div>

            <div>
              {' '}
              <MultiProgress
                transitionTime={0.5}
                elements={value.progress}
                backgroundColor="#F0F0F0"
                className={`${isWeb ? styles.StatsOverview__MultiProgress : ''} ${styles.StatsOverview__MultiProgress__Element}`}
                key={`${value.label}-progress-${index}`}
              />{' '}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default StatsOverview;